// src/lib/intake/intakeSession.js
//
// Ticket 061 (Advisor Intake I4). The two things the page does with the network,
// written as plain async functions that RETURN the action to dispatch. No React.
//
//   runSubmit  - first save (intake-start) or later save (intake-save)
//   runSaveScreen - the save on Next for a Section 2 or 3 screen (ticket 064)
//   runCalculate - the live results preview (intake-calculate, ticket 069)
//   runSaveResults - the explicit "Save my results" (intake-submit, ticket 069)
//   runShareStatus / runShare / runStopSharing - the advisor share (ticket 070, intake-share).
//                NEVER called in advisor mode (ticket 073): an advisor owns the client.
//   subjectId (ticket 073) - advisor mode only: the client the advisor owns, passed to every
//                intake call. Undefined in client mode, where nothing is sent.
//   runResume  - "continue where you left off": intake-load for each year the
//                Welcome would offer (plan D-OD1, option A)

import {
  startIntake, loadIntake, saveIntake, calculateIntake, submitIntake, getShareStatus, shareIntake, stopSharing,
} from './intakeApi'
import { buildSection1Domains } from './section1Facts'
import { buildSectionDomains } from './section23Facts'

const RESUMED_NOTICE = 'You had already started this intake, so we opened what you saved.'
const CONFLICT_NOTICE = 'This intake changed on another device. We loaded the latest version.'

// Maps an API result that is not a plain success to the action that shows it.
function failureAction(result, where) {
  switch (result.kind) {
    case 'PARTNER_ACCOUNT': return { type: 'advisor' }
    case 'NOT_AVAILABLE': return { type: 'unavailable' }
    case 'INVALID_TAX_YEAR': return { type: 'invalidYear' }
    default: return { type: 'failed', where, code: result.kind }
  }
}

export async function runSubmit({ accessToken, state, partnerRef, subjectId }) {
  const domains = buildSection1Domains(state.answers)
  const first = state.revision == null
  const result = first
    ? await startIntake({ accessToken, taxYear: state.taxYear, domains, partnerRef, subjectId })
    : await saveIntake({ accessToken, taxYear: state.taxYear, baseRevision: state.revision, domains, subjectId })

  if (result.kind === 'OK') {
    if (first && result.body && result.body.resumed) {
      return {
        type: 'applyIntake',
        taxYear: state.taxYear,
        revision: result.body.revision,
        facts: result.body.facts,
        suggestions: result.body.suggestions,
        notice: RESUMED_NOTICE,
      }
    }
    return { type: 'saved', revision: result.body.revision, suggestions: result.body.suggestions }
  }

  if (result.kind === 'REVISION_CONFLICT') {
    const latest = await loadIntake({ accessToken, taxYear: state.taxYear, subjectId })
    if (latest.kind === 'OK' && latest.body && latest.body.exists) {
      return {
        type: 'applyIntake',
        taxYear: state.taxYear,
        revision: latest.body.revision,
        facts: latest.body.facts,
        suggestions: latest.body.suggestions,
        notice: CONFLICT_NOTICE,
      }
    }
    return failureAction(latest.kind === 'OK' ? { kind: 'BAD_REQUEST' } : latest, 'submit')
  }

  return failureAction(result, 'submit')
}

// Save ONE Section 2 or 3 screen: intake-save with base_revision and only the
// domains that screen owns (income is sent whole). Always a later save, never
// intake-start. A failure other than the named ones keeps the person on the
// screen with their answers (screenSaveFailed); a stale revision reloads the
// latest intake through intake-load (same message as Section 1).
export async function runSaveScreen({ accessToken, state, subjectId }) {
  const domains = buildSectionDomains(state.screen, state.answers)
  if (domains === null) return { type: 'screenSaved', revision: state.revision, suggestions: state.suggestions }
  const result = await saveIntake({ accessToken, taxYear: state.taxYear, baseRevision: state.revision, domains, subjectId })

  if (result.kind === 'OK') {
    return { type: 'screenSaved', revision: result.body.revision, suggestions: result.body.suggestions }
  }
  if (result.kind === 'REVISION_CONFLICT') {
    const latest = await loadIntake({ accessToken, taxYear: state.taxYear, subjectId })
    if (latest.kind === 'OK' && latest.body && latest.body.exists) {
      return {
        type: 'applyIntake',
        taxYear: state.taxYear,
        revision: latest.body.revision,
        facts: latest.body.facts,
        suggestions: latest.body.suggestions,
        notice: CONFLICT_NOTICE,
      }
    }
    return { type: 'screenSaveFailed', code: latest.kind === 'OK' ? 'BAD_REQUEST' : latest.kind }
  }
  if (result.kind === 'PARTNER_ACCOUNT') return { type: 'advisor' }
  if (result.kind === 'NOT_AVAILABLE') return { type: 'unavailable' }
  if (result.kind === 'INVALID_TAX_YEAR') return { type: 'invalidYear' }
  return { type: 'screenSaveFailed', code: result.kind }
}

// years: the tax years to look in (the Welcome's list). Newest first in the result.
export async function runResume({ accessToken, years, subjectId }) {
  const sorted = [...years].sort((a, b) => b - a)
  const results = await Promise.all(sorted.map((taxYear) => loadIntake({ accessToken, taxYear, subjectId })))

  const blocking = results.find((r) => r.kind === 'PARTNER_ACCOUNT')
  if (blocking) return { type: 'advisor' }
  const off = results.find((r) => r.kind === 'NOT_AVAILABLE')
  if (off) return { type: 'unavailable' }
  const broken = results.find((r) => r.kind !== 'OK')
  if (broken) return { type: 'failed', where: 'resume', code: broken.kind }

  const candidates = []
  results.forEach((r, i) => {
    if (r.body && r.body.exists && r.body.started) {
      candidates.push({ taxYear: sorted[i], revision: r.body.revision, facts: r.body.facts, suggestions: r.body.suggestions })
    }
  })
  return { type: 'candidates', candidates }
}

// ── Results (ticket 069) ────────────────────────────────────────────────────
// Live preview: reads the SAVED facts on the server, writes nothing.
export async function runCalculate({ accessToken, taxYear, subjectId }) {
  const result = await calculateIntake({ accessToken, taxYear, subjectId })
  // A reply without the blocks the page draws is treated as a failure, not drawn.
  if (result.kind === 'OK' && result.body && result.body.meta && result.body.snapshot) {
    return { type: 'resultsLoaded', results: result.body }
  }
  if (result.kind === 'PARTNER_ACCOUNT') return { type: 'advisor' }
  if (result.kind === 'NOT_AVAILABLE') return { type: 'unavailable' }
  if (result.kind === 'INVALID_TAX_YEAR') return { type: 'invalidYear' }
  return { type: 'resultsFailed', code: result.kind === 'OK' ? 'BAD_RESPONSE' : result.kind }
}

// "Save my results": freezes the revision the person saw. already_submitted (a
// retry or double click at the same revision) counts as saved. A stale revision
// reloads the latest intake, which re-triggers the calculation.
export async function runSaveResults({ accessToken, taxYear, baseRevision, subjectId }) {
  const result = await submitIntake({ accessToken, taxYear, baseRevision, subjectId })
  if (result.kind === 'OK') return { type: 'resultsSaved', revision: baseRevision }
  if (result.kind === 'REVISION_CONFLICT') {
    const latest = await loadIntake({ accessToken, taxYear, subjectId })
    if (latest.kind === 'OK' && latest.body && latest.body.exists) {
      return {
        type: 'applyIntake',
        taxYear,
        revision: latest.body.revision,
        facts: latest.body.facts,
        suggestions: latest.body.suggestions,
        notice: CONFLICT_NOTICE,
      }
    }
    return { type: 'saveResultsFailed', code: latest.kind === 'OK' ? 'BAD_REQUEST' : latest.kind }
  }
  if (result.kind === 'PARTNER_ACCOUNT') return { type: 'advisor' }
  if (result.kind === 'NOT_AVAILABLE') return { type: 'unavailable' }
  if (result.kind === 'INVALID_TAX_YEAR') return { type: 'invalidYear' }
  return { type: 'saveResultsFailed', code: result.kind }
}

// ── Share with the advisor (ticket 070) ──────────────────────────────────────
function statusFromBody(body) {
  return {
    advisorName: body.advisor_name,
    shared: body.shared === true,
    sharedAt: body.shared_at || null,
    savedRevision: typeof body.saved_revision === 'number' ? body.saved_revision : null,
    newerSaved: body.newer_saved === true,
  }
}

const validStatus = (r) => r.kind === 'OK' && r.body && typeof r.body.advisor_name === 'string' && r.body.advisor_name.length > 0

// A failure of any kind (no linked advisor, function not deployed, network) means
// "no share entry"; Results are never affected.
export async function runShareStatus({ accessToken, taxYear }) {
  const result = await getShareStatus({ accessToken, taxYear })
  if (validStatus(result)) return { type: 'shareStatusLoaded', status: statusFromBody(result.body) }
  return { type: 'shareStatusUnavailable' }
}

// Shares the saved version at factsRevision. On success the status is read again so
// the page shows the server's date; if that read fails the reducer marks it shared.
export async function runShare({ accessToken, taxYear, factsRevision, contact }) {
  const result = await shareIntake({ accessToken, taxYear, factsRevision, contact })
  if (result.kind === 'OK') {
    const latest = await getShareStatus({ accessToken, taxYear })
    return {
      type: 'shareDone',
      status: validStatus(latest) ? statusFromBody(latest.body) : null,
      sharedAt: result.body && result.body.shared_at ? result.body.shared_at : null,
    }
  }
  return { type: 'shareFailed', code: result.kind }
}

export async function runStopSharing({ accessToken, taxYear }) {
  const result = await stopSharing({ accessToken, taxYear })
  if (result.kind === 'OK') {
    const latest = await getShareStatus({ accessToken, taxYear })
    return { type: 'stopDone', status: validStatus(latest) ? statusFromBody(latest.body) : null }
  }
  return { type: 'shareFailed', code: result.kind }
}
